import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, ConfirmModal } from '@microservices/wiskey-react-components'
import { DataGrid, GridRowParams } from '@microservices/wiskey-react-table-component'
import { Box, Grid, Typography } from '@mui/material'
import { DataGridProps } from '@mui/x-data-grid'

import { useConfigTimeLines } from '@pages/ConfigTimeLines/hooks/useConfigTimeLines'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { ExportActionsPanel } from '@components/ConfigActionsPanel'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { CopyDialog } from '@components/CopyDialog'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { PageTitle } from '@components/PageTitle'
import { ExportAllCheckbox } from '@components/ui/ExportAllCheckbox'

import { EditConfigProvider, useEditConfig } from '@providers/EditConfigProvider'

import { generateConfigSelectColumn, generateDataGridActionsColumn } from '@helpers'
import { CONFIG_MENU_OPTION, ENTITY_CONFIG_TYPE, FLEXIBLE, ROUTES } from '@constants'

export const ConfigTimeLines: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { configMenuOption, selectedConfigId, handleSelectConfigId } = useEditConfig()

  const columns: DataGridProps['columns'] = [
    {
      field: 'code',
      headerName: t('diagrams.internalId'),
      flex: 1,
    },
    {
      field: 'title',
      headerName: t('diagrams.title'),
      flex: 1,
    },
  ]

  const { state, data, handlers } = useConfigTimeLines()

  const { showCopyDialog, isLoadingCopy, showDeleteModal, isLoadingDelete, isLoadingAllTimeLines } =
    state

  const { timelines } = data

  const { copyData } = state

  const {
    handleCloseCopyDialog,
    handleCopyTimeLine,
    handleClickCreateTimeLine,
    handleEditTimeLine,
    handleShowCopyDialog,
    handleShowDeleteModal,
    handleCloseDeleteModal,
    handleDeleteTimeLine,
  } = handlers

  const columnsWithActions = useMemo(
    () => [
      ...columns,
      configMenuOption &&
        configMenuOption !== CONFIG_MENU_OPTION.IMPORT &&
        generateConfigSelectColumn({
          selectedConfigId,
          onSelectRow: handleSelectConfigId,
        }),
      generateDataGridActionsColumn({
        onEdit: params => handleEditTimeLine(params.row.id),
        onCopy: params => handleShowCopyDialog(params.row.id, params.row.title),
        onDelete: params => handleShowDeleteModal(params.row.id),
      }),
    ],
    [columns, selectedConfigId, configMenuOption]
  )

  const handleRowClick = (params: GridRowParams) => {
    navigate(ROUTES.TIME_LINE_GRID + `/${params.row.id}`)
  }

  return (
    <>
      <EditConfigScriptDialog />
      {showCopyDialog && copyData && (
        <CopyDialog
          hideObject
          copyData={copyData}
          dialogTitle={t(`copying.timeline.dialogTitle`)}
          isShow={showCopyDialog}
          loading={isLoadingCopy}
          models={timelines?.map(timeline => timeline.code) || []}
          onClose={handleCloseCopyDialog}
          onCopy={handleCopyTimeLine}
        />
      )}
      <ConfirmModal
        actionBtnText={t('modal.delete.btn')}
        isShow={showDeleteModal}
        text={t('modal.delete.warning')}
        title={t('modal.delete.title')}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteTimeLine}
      />
      <PageTitle
        end={
          <Box sx={{ py: 0.5 }}>
            <EditConfigMenu />
          </Box>
        }
        start={
          <>
            <Typography display={'inline'} sx={{ mr: 1, ml: 1 }} variant={'h6'}>
              {t('pageTitle.timeLines')}
            </Typography>
          </>
        }
      />
      <PageContentLayout sx={{ ...FLEXIBLE, p: 1 }}>
        <ExportAllCheckbox />
        <Grid container alignItems='center' flexWrap='nowrap' gap={1}>
          <Grid item>
            <Button
              sx={{ mb: 1, width: 150 }}
              variant={'contained'}
              onClick={handleClickCreateTimeLine}
            >
              {t(`timeLines.add`)}
            </Button>
          </Grid>
        </Grid>
        <DataGrid
          hideFooter
          columns={columnsWithActions}
          loading={isLoadingDelete || isLoadingCopy || isLoadingAllTimeLines}
          rows={timelines || []}
          sx={FLEXIBLE}
          onRowClick={handleRowClick}
        />
        <ExportActionsPanel />
      </PageContentLayout>
    </>
  )
}

export const ConfigTimeLinesWithProvider: FC = () => {
  return (
    <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.TIMELINES}>
      <ConfigTimeLines />
    </EditConfigProvider>
  )
}
